import React, {useState} from 'react';
import {Zone, ZoneData} from "types/Zone";
import {Form, Formik, FormikHelpers} from "formik";
import {useNavigate} from "react-router-dom";
import Label from "components/Form/Label";
import Field from "components/Form/Field";
import Button from "components/Button";
import Selector from "components/Form/Selector";
import IconName from "types/IconName";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Locator from "components/Form/Locator";
import Toggle from "components/Form/Toggle";
import {useOperator} from "context/OperatorContext";
import AccessibilitySelector from "components/Form/AccessibilitySelector/AccessibilitySelector";
import {OpeningHoursFieldSet} from "../../../components/Form/Form/Fieldset";
import {useAuth} from "../../../context/AuthContext";
import {Geometry} from "geojson";
import * as Yup from "yup";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {ExclamationCircleIcon} from "@heroicons/react/solid";
import {TextAreaField} from "../../../components/Form/Form";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";

// Types ----

type ZoneFormProps = {
  zone?: Zone
  onSubmit: (values: ZoneData, helpers: FormikHelpers<ZoneData>) => Promise<any>
}

// Component ----

function ZoneForm({onSubmit, zone}: ZoneFormProps) {
  const {termFor} = useOperator();
  const auth = useAuth();
  const navigate = useNavigate();

  const {data: sites} = useQuery(
    ['sites.all', {page: 1}],
    () => ngage.sites.all(1),
    {enabled: auth.can('sites:read')}
  );

  const [siteId, setSiteId] = useState<number | null>(zone?.site.id || null);
  const [mapFlyToGeometry, setMapFlyToGeometry] = useState<Geometry | undefined>();

  const {data: levels, isFetching: fetchingLevels} = useQuery(
    ['levels', {siteId, page: 1}],
    () => ngage.levels.all(Number(siteId), {page: 1}),
    {enabled: !!siteId && auth.can('levels:read')}
  );

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").max(255, "Maximum of 255 characters"),
    description: Yup.string().max(50000, "Maximum character limit exceeded"),
    site_id: Yup.number().notOneOf([0], 'This field is required').required("This field is required"),
    level_id: Yup.number().notOneOf([0], 'This field is required').required("This field is required"),
    area: Yup.object().shape({
      coordinates: Yup.array().of(
        Yup.array().of(
          Yup.array().of(Yup.number())
        )
      ).required(`Please draw the ${termFor("zone")} area on the map using the drawing tool in the top left corner`),
    }),
    restriction_reason: Yup.string().when('is_restricted', ([isRestricted]: any, schema: any) => {
      if (isRestricted) {
        return schema.required('This field is required');
      }
      return schema;
    })
  })

  const initialValues: ZoneData = {
    site_id: zone?.site.id || 0,
    level_id: zone?.level?.id || 0,
    name: zone?.name || '',
    description: zone?.description || '',
    accessibility_criteria: zone?.accessibility_criteria || [],
    is_sustainable: zone?.is_sustainable ?? false,
    hours: zone?.hours || null,
    area: zone?.area || undefined,
    is_mapped: zone?.is_mapped ?? false,
    is_pinned: zone?.is_pinned ?? false,
    is_hidden: zone?.is_hidden ?? false,
    is_restricted: zone?.is_restricted ?? false,
    restriction_reason: zone?.restriction_reason || '',
  }

  function selectSite(siteId: number | string, {setFieldValue}: FormikHelpers<any>) {
    void setFieldValue('site_id', Number(siteId));
    setSiteId(Number(siteId));

    // Find the site and fly to it on the map
    const site = sites?.data.find(site => site.id === Number(siteId));
    setMapFlyToGeometry(site?.area || zone?.area);
  }

  function selectLevel(levelId: number | string, {setFieldValue}: FormikHelpers<any>) {
    void setFieldValue('level_id', Number(levelId));
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />

            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
  
                  {/* Key Info */}
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Key Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Basic details about the {termFor('zone')}
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-4">
                        <div className="mt-1">
                          <Field
                            type="text"
                            name="name"
                            id="name"
                            label='Name *'
                            hint='255 chars max'
                            autoComplete="off"
                            maxLength="255"
                            autoFocus
                          />
                        </div>
                      </div>
  
                      <div className="sm:col-span-6">
                        <div className="mt-1">
                          <TextAreaField
                            rows={5}
                            name="description"
                            id="description"
                            label="Description"
                            hint="Optional. 50,000 chars max."
                            autoComplete="off"
                            maxChars={50000}
                          />
                        </div>
                      </div>
  
                      {/* Accessibility */}
                      <div className="sm:col-span-6">
                        <AccessibilitySelector
                          selectedIds={initialValues.accessibility_criteria}
                          onChange={criteria => formik.setFieldValue('accessibility_criteria', criteria)}
                        />
                      </div>
  
                      {/* Sustainability */}
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Sustainable"
                          description={`Whether this ${termFor('zone')} is environmentally and socially sustainable`}
                          checked={formik.values.is_sustainable}
                          onChange={checked => formik.setFieldValue('is_sustainable', checked)}
                        />
                      </div>
                    </div>
                  </div>
  
                  {/* Opening Hours */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Opening Hours
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        When the {termFor('zone')} is open for visitors
                      </p>
                    </div>
                    <div className="mt-6">
                      <OpeningHoursFieldSet name="hours" canInherit/>
                    </div>
                  </div>
  
                  {/* Location */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Location
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Set where the {termFor('zone')} is located
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <Selector
                          label={`${termFor('Site')} *`}
                          items={sites?.data.map(site => ({
                            id: site.id,
                            name: site.name,
                            icon: IconName.OFFICE_BUILDING
                          })) || []}
                          selectedId={initialValues.site_id}
                          onChange={selected => selectSite(selected.id, formik)}
                        />

                        {(!!formik.errors?.site_id && !!formik.touched?.site_id) &&
                          <FieldHelp isError>
                            {formik.errors.site_id}
                          </FieldHelp>
                        }
                      </div>
  
                      <div className="sm:col-span-3">
                        <Selector
                          label="Level *"
                          isLoading={fetchingLevels}
                          items={levels?.data.map(level => ({
                            id: level.id,
                            name: level.name,
                            icon: IconName.SWITCH_VERTICAL
                          })) || []}
                          selectedId={initialValues.level_id}
                          onChange={selected => selectLevel(selected.id, formik)}
                          disabled={!siteId}
                        />

                        {(!!formik.errors?.level_id && !!formik.touched?.level_id) &&
                          <FieldHelp isError>
                            {formik.errors.level_id}
                          </FieldHelp>
                        }
                      </div>
  
                      <div className="sm:col-span-6 rounded-md overflow-hidden">
                        <span className="block text-sm font-medium text-gray-700">
                            Geographic Area *
                        </span>
                        <div className="mt-1">
                          <Locator
                            height={'32rem'}
                            type='area'
                            geometry={initialValues.area}
                            flyToGeometry={mapFlyToGeometry}
                            onChange={geometry => formik.setFieldValue('area', geometry)}
                          />
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                          Draw the geographic area for this {termFor('zone')} on the map using the drawing tool
                        </p>

                        {/* @ts-ignore */}
                        {(!!formik.errors.area?.coordinates && formik.touched.area) &&
                          <FieldHelp isError>
                            <div className="flex items-center gap-2">
                              <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true"/>
                              {/* @ts-ignore */}
                              {formik.errors.area.coordinates}
                            </div>
                          </FieldHelp>
                        }
                      </div>
                    </div>
                  </div>
  
                  {/* Mapping */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Mapping
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Choose how visible this {termFor('zone')} is on the map
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Restricted Area"
                          description="Show this as a restricted area, and avoid it when directing visitors"
                          checked={formik.values.is_restricted}
                          onChange={checked => {
                            void formik.setFieldValue('is_restricted', checked)
                          }}
                          multiline
                        />
                      </div>
  
                      {formik.values.is_restricted &&
                        <div className="sm:col-span-6">
                          <Label htmlFor="name">Restriction Reason *</Label>
                          <div className="mt-1">
                            <Field
                              type="text"
                              name="restriction_reason"
                              id="restriction_reason"
                              autoComplete="off"
                              maxLength="255"
                            />
                          </div>
                        </div>
                      }
  
                      <div className="sm:col-span-3">
                        <Toggle
                          label="Show area"
                          description="Display this as an area on the map"
                          checked={formik.values.is_mapped}
                          onChange={checked => {
                            void formik.setFieldValue('is_mapped', checked)
                            if (!checked) void formik.setFieldValue('is_pinned', false)
                          }}
                          multiline
                        />
                      </div>
  
                      {formik.values.is_mapped &&
                        <div className="sm:col-span-3">
                          <Toggle
                            label="Show pin"
                            description="Pin the centre of this area on the map"
                            checked={formik.values.is_pinned}
                            onChange={checked => formik.setFieldValue('is_pinned', checked)}
                            multiline
                          />
                        </div>
                      }
                    </div>
                  </div>

                  {/* Visibility */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Visibility
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Set the visibility of this {termFor('zone')}
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Hide from Listings"
                          description={`Hides this ${termFor('zone')} from any listings in the audience app, such as "Explore"`}
                          checked={formik.values.is_hidden}
                          onChange={checked => formik.setFieldValue('is_hidden', checked)}
                          multiline
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
  
              <div className="px-4 py-5 bg-gray-50 text-right sm:px-6 sm:rounded-b-md">
                <div className="flex space-x-3 justify-end">
                  <Button color={Button.colors.WHITE} onClick={() => navigate(-1)} disabled={formik.isSubmitting}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    showSpinner={formik.isSubmitting}
                    disabled={formik.isSubmitting}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  );
}

// Export ----

export default ZoneForm;